<template>
  <DataTable
    class="p-datatable-sm"
    stripedRows
    showGridlines
    filterDisplay="menu"
    responsiveLayout="scroll"
    rowHover
    lazy
    :value="i18nList"
    >
    <template #header>
      <div class="flex justify-content-end">
        <div class="mb-0 mr-4">
          <div class="field grid center mb-0">
            <LanguageSelector v-model="language" v-bind:excludedLanguages="i18nKeys" compact />
          </div>
        </div>
        <div class="mb-0 mr-2">
          <div class="field grid center mb-0">
            <Button
            :label="$t('buttons.addTranslation')"
            icon="pi pi-plus"
            iconPos="right"
            :disabled="!language"
            @click="initI18nLanguage"
            />
          </div>
        </div>

      </div>
    </template>

    <template #empty>
      No translations
    </template>

    <template #loading>
      Loading translations. Please wait.
    </template>

    <Column style="min-width: 120px;">
      <template #header>
        <div class="flex justify-content-center w-full">
          <div>Language</div>
        </div>
      </template>
      <template #body="slotProps">
        <div class="flex justify-content-start pl-4 align-items-center w-full">
          <Flag :id="slotProps.data.id" />
          <span class="ml-3">{{languageById(slotProps.data.id)?.name}}</span>
        </div>
      </template>
    </Column>
    <Column>
      <template #header>
        <div class="flex justify-content-center w-full">
          <div>{{ $t('forms.firstname') }}</div>
        </div>
      </template>
      <template #body="slotProps">
        <div class="flex justify-content-start w-full">
          <div class="w-full">
            <InputText class="w-full" :modelValue="i18n[slotProps.data.id].firstname" v-debounce:300ms="(val) => changeFirstname(slotProps.data.id, val)" placeholder="Firstname" />
          </div>
        </div>
      </template>
    </Column>
    <Column>
      <template #header>
        <div class="flex justify-content-center w-full">
          <div>{{ $t('forms.lastname') }}</div>
        </div>
      </template>
      <template #body="slotProps">
        <div class="flex justify-content-start w-full">
          <div class="w-full">
            <InputText class="w-full" :modelValue="i18n[slotProps.data.id].name" v-debounce:300ms="(val) => changeLastname(slotProps.data.id, val)" :placeholder="$t('forms.lastname')" />
          </div>
        </div>
      </template>
    </Column>
    <Column>
      <template #header>
        <div class="flex justify-content-center w-full">
          <div>{{ $t('forms.actions') }}</div>
        </div>
      </template>
      <template #body="slotProps">
        <div class="flex justify-content-center w-full">
          <Button
            v-if="!isDefaultLanguage(slotProps.data.id)"
            icon="pi pi-trash"
            class="p-button-rounded p-button-danger p-button-outlined"
            @click="confirmRemove(slotProps.data.id)"
          />
        </div>
      </template>
    </Column>
  </DataTable>
</template>

<script>
import { ref } from 'vue'

import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import InputText from 'primevue/inputtext'
import { mapGetters } from 'vuex'
import Flag from '@/components/resource/Flag.vue'
import LanguageSelector from '@/components/resource/LanguageSelector.vue'
import { getDirective as vueDebounce } from 'vue-debounce'

import { useFlowEditor } from '@/compositions'

export default {
  components: {
    DataTable, Column, InputText,
    LanguageSelector,
    Flag
  },
  directives: {
    debounce: vueDebounce(3)
  },
  props: { editorKey: String },
  setup (props) {
    const language = ref('')
    return {
      language,
      ...useFlowEditor(props.editorKey)
    }
  },
  computed: {
    ...mapGetters('resource', ['languageById']),
    i18n: {
      get () { return this.fieldGet('i18n') },
      set (val) { this.fieldSet({field: 'i18n', value: val}) }
    },
    i18nKeys: {
      get () { return Object.keys(this.i18n || {})}
    },
    i18nList: {
      get () { return this.i18nKeys.map((key) => ({id: key}))}
    }
  },
  methods: {
    isDefaultLanguage (language) {
      return this.fieldGet('defaultLanguage.id') === language
    },

    initI18nLanguage () {
      this.i18n[this.language] = {
        firstname: '',
        name: ''
      }

      this.fieldObjSet({field: 'i18n', key: this.language, value: this.i18n[this.language]})
      this.language = null
      window.scrollTo(0, document.body.scrollHeight)
    },
    changeFirstname (language, value) {
      this.fieldSet({field: `i18n.${language}.firstname`, value})
      if (this.isDefaultLanguage(language)) {
        this.fieldSet({field: 'firstname', value})
      }
    },
    changeLastname (language, value) {
      this.fieldSet({field: `i18n.${language}.name`, value})
      if (this.isDefaultLanguage(language)) {
        this.fieldSet({field: 'name', value})
      }
    },
    confirmRemove (language) {
      this.$confirm.require({
        message: `Are you sure you want to delete this translation?`,
        header: `Delete ${this.languageById(language)?.name || 'Unknown'}`,
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.fieldObjDel({field: `i18n`, key: language})
        },
        reject: () => {}
      })
    },
  },
  watch: {
    'doc.name': function (newName) {
      const docId = this.doc.id
      let title = `${this.doc.firstname} ${newName}`
      this.$breadcrumb.setCurrent(title, [
        {label: 'Resources', disabled: true},
        {label: 'Actors', disabled: false, to: '/resources/actors'},
        {label: title, disabled: true}
      ], {caption: '', badge: docId === 'new' ? 'Draft' : ''})
    },
    'doc.firstname': function (newFirstName) {
      const docId = this.doc.id
      let title = `${newFirstName} ${this.doc.name}`
      this.$breadcrumb.setCurrent(title, [
        {label: 'Resources', disabled: true},
        {label: 'Actors', disabled: false, to: '/resources/actors'},
        {label: title, disabled: true}
      ], {caption: '', badge: docId === 'new' ? 'Draft' : ''})
    }
  }
}
</script>
